import { DataSourceType, LicenseRequestGateType, PlanToJoin } from "@coeff/api";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";

import {
  BulletCheckList,
  Button,
  CoePlanIcon,
  FlexColumn,
  Tooltip,
  Typography,
} from "../../../components";
import { COLORS } from "../../../constants";
import { AppState } from "../../../store";
import { useApiContext, useTrack } from "../../../utils";
import { dataSourceTypeToCommaSeparatedString } from "../billing.utils";
import {
  daysSinceLicenseRequest,
  formatPlanManagerNames,
  formatRequestedAgo,
  getActiveLicenseRequestDate,
  getRequestLicenseFeatures,
  splitIntoHoverWords,
} from "../planToJoin.utils";

import { RequestSentModal } from "./RequestSentModal";

// ENG-8924: Plans & Billing for a seatless user whose company already has a plan they can join
// (the ENG-8920 plan to join). Also the landing page for the countdown emails' Request License
// button (ENG-8861): the email links to Plans & Billing, which renders this page.

const Layout = styled.div`
  display: grid;
  grid-template-columns: 1fr 426px;
  width: 100%;
  height: 100%;
`;

const LogoContainer = styled.div`
  background: linear-gradient(
    103deg,
    #428bfa 0%,
    #a769e6 35.27%,
    #d850b8 60.74%,
    #ff3353 78.38%,
    #ff8121 96.5%
  );
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: ${COLORS.white};
`;

// Wide enough that the copy doesn't break early (Kurt's 9/30 review).
const Content = styled(FlexColumn)`
  max-width: 640px;
`;

const PlanManagersTrigger = styled.span`
  text-decoration: underline dotted;
  cursor: help;
`;

// "your plan manager (Tom)" / "your plan managers (Tom, Dan & 2 more)", with every manager's email
// in a light tooltip below the names, with the design's shadow (10/2 review). Used in the page copy
// and the Request Sent confirmation.
const PlanManagersMention: React.FC<{ managers: PlanToJoin["plan_managers"] }> = ({ managers }) => {
  const tooltip = (
    <FlexColumn gap={0.25}>
      <Typography variant={"body2"} fontWeight={"bold"}>
        {managers.length === 1 ? "Plan manager:" : "Plan managers:"}
      </Typography>
      {managers.map(manager => (
        <Typography key={manager.email} variant={"body2"} style={{ color: COLORS.black65 }}>
          {manager.email}
        </Typography>
      ))}
    </FlexColumn>
  );
  return (
    <>
      your {managers.length === 1 ? "plan manager" : "plan managers"} (
      {/* Wraps inside the parentheses, with the tooltip centered under the hovered word (see
          splitIntoHoverWords). */}
      {splitIntoHoverWords(formatPlanManagerNames(managers)).map((word, i) => (
        <Tooltip
          key={i}
          title={tooltip}
          variant="light"
          placement="bottom"
          overlayInnerStyle={{
            // Black at 12% opacity, not gray (Ian, 10/7).
            boxShadow: "0px 0px 16px rgba(0, 0, 0, 0.12), 0px 16px 24px rgba(0, 0, 0, 0.12)",
            padding: "8px 12px",
          }}
        >
          <PlanManagersTrigger>{word}</PlanManagersTrigger>
        </Tooltip>
      ))}
      )
    </>
  );
};

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  width: 160px;
  margin-top: 16px;
`;

// Wider than the button; left-aligned with the copy above rather than overhanging it.
const PurchaseOwnPlanLink = styled(Button)`
  align-self: flex-start;
  padding: 0;
`;

type Props = {
  gateType: typeof LicenseRequestGateType.General | typeof LicenseRequestGateType.PremiumConnector;
  planToJoin: PlanToJoin;
  premiumConnectorsUsed: DataSourceType[];
  // The page's `?from=` param, e.g. "expansion_email_1" from a countdown email's Request License
  // link, so analytics can tell which email drove the request.
  eventFrom?: string;
  // ENG-9319: set when the user may also buy their own plan (a Pro team plan, general gate). Shows
  // "Or, purchase your own plan" under Request License; the caller switches to the plan chooser.
  onPurchaseOwnPlan?: () => void;
};

export const RequestLicenseFullScreen: React.FC<Props> = ({
  gateType,
  planToJoin,
  premiumConnectorsUsed,
  eventFrom,
  onPurchaseOwnPlan,
}) => {
  const track = useTrack();
  const { apiClient } = useApiContext();
  const clientFlags = useSelector((state: AppState) => state.app.clientFlags);
  const [sending, setSending] = useState(false);
  // 10/1 design review: a request under 7 days old greys the button out as "Requested N days ago";
  // after that it's active again, and a new request re-emails the managers.
  const [requestedAt, setRequestedAt] = useState<Date | undefined>(
    getActiveLicenseRequestDate(clientFlags?.pending_license_request_dt)
  );
  const [showSentModal, setShowSentModal] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const isPremiumGate = gateType === LicenseRequestGateType.PremiumConnector;
  const planManagers = planToJoin.plan_managers;
  const eventProps = {
    // Where the user came from when the link says so (Kurt, ENG-8851), as billing_plan_chooser_viewed
    // does; otherwise this page.
    event_from: eventFrom || "webapp_billing_request_license",
    license_request_gate_type: gateType,
  };

  useEffect(() => {
    track("request_license_modal_shown", {
      ...eventProps,
      // ENG-9319: whether "Or, purchase your own plan" was offered alongside Request License.
      routing_outcome: onPurchaseOwnPlan
        ? "request_license_or_purchase_own_plan"
        : "request_license",
      plan_managers_present: true,
      // 10/2 review: how often users come back while a request is still pending.
      license_requested_days_ago: clientFlags?.pending_license_request_dt
        ? daysSinceLicenseRequest(new Date(clientFlags.pending_license_request_dt))
        : undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRequestLicense = async () => {
    setSending(true);
    setError(undefined);
    track("request_license_clicked", eventProps);
    try {
      await apiClient.requestLicense({
        request_license_request: {
          data_source_type: isPremiumGate ? premiumConnectorsUsed[0] : undefined,
          gate_type: gateType,
        },
      });
      setRequestedAt(new Date());
      setShowSentModal(true);
      track("request_license_sent", eventProps);
    } catch (e) {
      setError("Something went wrong sending your request. Please try again.");
      track("request_license_failed", eventProps);
    } finally {
      setSending(false);
    }
  };

  const handlePurchaseOwnPlan = () => {
    // The existing upgrade click, tagged with the gate, so the gate events show which option the
    // user picked.
    track("upgrade_to_pro_clicked", { ...eventProps, cta_type: "purchase_own_plan" });
    onPurchaseOwnPlan?.();
  };

  const headline =
    isPremiumGate && premiumConnectorsUsed.length > 0
      ? `Upgrade your plan to import from ${dataSourceTypeToCommaSeparatedString(
          premiumConnectorsUsed,
          2
        )}.`
      : "Upgrade your plan to start automating your spreadsheets.";

  const features = getRequestLicenseFeatures(
    gateType,
    premiumConnectorsUsed.length > 0
      ? dataSourceTypeToCommaSeparatedString(premiumConnectorsUsed, 2)
      : undefined
  );

  return (
    <Layout>
      <Content gap={2} padding={"40px"}>
        {/* One "Upgrade" title for every gate page (10/1 design review). */}
        <Typography variant={"h4"} fontWeight={"bold"}>
          Upgrade
        </Typography>
        <Typography variant={"body1"}>{headline}</Typography>
        <Typography variant={"body1"}>
          Your workspace already has a paid plan. Send a request to{" "}
          <PlanManagersMention managers={planManagers} /> to be added to your company&apos;s plan.
        </Typography>

        <BulletCheckList
          items={features.map(feature => (
            <Typography key={feature} variant={"body2"}>
              {feature}
            </Typography>
          ))}
        />

        {error && (
          <Typography variant={"body2"} style={{ color: COLORS.red }}>
            {error}
          </Typography>
        )}

        {/* Request License is the primary action; "Or, purchase your own plan" is the secondary
            link under it on a Pro team plan (ENG-9319, per the design). */}
        <Actions>
          <Button
            type={"primary"}
            loading={sending}
            disabled={Boolean(requestedAt)}
            onClick={handleRequestLicense}
          >
            {requestedAt ? formatRequestedAgo(requestedAt) : "Request License"}
          </Button>
          {onPurchaseOwnPlan && (
            <PurchaseOwnPlanLink type={"link"} onClick={handlePurchaseOwnPlan}>
              Or, purchase your own plan
            </PurchaseOwnPlanLink>
          )}
        </Actions>
      </Content>
      <LogoContainer>
        <CoePlanIcon height={88} width={76} fill={COLORS.white} />
      </LogoContainer>
      <RequestSentModal
        visible={showSentModal}
        message={
          <>
            Your request was sent to <PlanManagersMention managers={planManagers} />. They can add
            you to your company&apos;s plan.
          </>
        }
        onClose={() => setShowSentModal(false)}
      />
    </Layout>
  );
};
